import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const AuthContext = createContext(null);

const getDeviceId = () => {
  let id = localStorage.getItem("device_id");
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("device_id", id);
  }
  return id;
};

const getDeviceName = () => {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Windows/.test(ua) ? "Windows" : /Mac OS/.test(ua) ? "Mac" : /Linux/.test(ua) ? "Linux" : "Unknown OS";
  return `${browser} on ${os}`;
};

const IDLE_LIMIT_MS = 60 * 60 * 1000;
const touch = () => localStorage.setItem("last_activity", String(Date.now()));
const idleExpired = () => Date.now() - Number(localStorage.getItem("last_activity") || 0) > IDLE_LIMIT_MS;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null = loading, false = logged out
  const [checking, setChecking] = useState(true);

  const logout = useCallback(async (reason = "manual") => {
    try { await api.post("/auth/logout", { reason }); } catch (e) { /* token may already be invalid */ }
    localStorage.removeItem("token");
    setUser(false);
    if (reason === "inactivity") toast.info("You were signed out after 1 hour of inactivity.");
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(false);
      setChecking(false);
      return;
    }
    if (idleExpired()) {
      if (window.__idleLogoutRan) return;
      window.__idleLogoutRan = true;
      logout("inactivity").finally(() => { setChecking(false); window.__idleLogoutRan = false; });
      return;
    }
    api
      .get("/auth/me")
      .then((r) => { touch(); setUser(r.data); })
      .catch(() => {
        localStorage.removeItem("token");
        setUser(false);
      })
      .finally(() => setChecking(false));
  }, [logout]);

  useEffect(() => {
    if (!user) return;
    let last = 0;
    const onActivity = () => { const now = Date.now(); if (now - last > 15000) { last = now; touch(); } };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const timer = setInterval(() => { if (idleExpired()) logout("inactivity"); }, 30000);
    const onUnauthorized = () => { localStorage.removeItem("token"); setUser(false); };
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(timer);
      window.removeEventListener("auth:unauthorized", onUnauthorized);
    };
  }, [user, logout]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password, device_id: getDeviceId(), device_name: getDeviceName() });
    localStorage.setItem("token", data.token);
    touch();
    setUser(data.user);
    return data.user;
  };

  const isManagement = user && user.role === "management";

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, isManagement, apiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
